import React, { useEffect, useRef } from "react";
import { ChevronDown, FolderInput, Library, Pencil, Trash2 } from "lucide-react";

import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { CrumbSlash, OrgCrumb, crumbCls, crumbRowCls, currentCrumbCls } from "@/shared/components/Breadcrumb";
import { AddItems, menuCls, type AddKind } from "./KbAddMenu";
import { useInlineRename } from "./hooks/useInlineRename";
import type { NodeEdits } from "./KbRow";

import type { KbNode, KnowledgeBase } from "@/shared/types";

const itemCls = "gap-2 px-2 py-[5px] rounded-[8px] text-[13px] cursor-pointer focus:bg-[var(--accent-hover)]";

export interface PlaceActions {
  onAdd: (kind: AddKind) => void;
  edits: NodeEdits;
  onEditBase: () => void;
  onDeleteBase: () => void;
}

/** The open folder (or the base at its top) with its menu: add to it, then manage it. */
function Place({ base, folder, actions }: { base: KnowledgeBase; folder?: KbNode; actions: PlaceActions }) {
  const rename = useInlineRename(folder?.name ?? "", (name) => folder && actions.edits.rename(folder, name));
  // Deferred past Radix's close and focus restore, so the dialog or the input takes focus
  const later = (fn: () => void) => () => setTimeout(fn, 0);
  if (rename.editing) {
    return <input {...rename.inputProps} className="min-w-0 w-[220px] h-7 px-2 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-focus)] outline-none text-[13.5px] font-medium text-[var(--text-primary)]" />;
  }
  const name = folder?.name ?? base.name;
  const icon = "w-3.5 h-3.5";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-label={`${name}, actions`} title={name} className={`press ${currentCrumbCls} rounded-[8px] hover:bg-[var(--accent-hover)] data-[state=open]:bg-[var(--accent-active)]`}>
          {!folder && base.icon && <span className="shrink-0 text-[13px] leading-none">{base.icon}</span>}
          <span className="truncate">{name}</span>
          <ChevronDown className="w-3 h-3 shrink-0 text-[var(--text-tertiary)]" strokeWidth={2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={`w-[272px] ${menuCls}`} onCloseAutoFocus={(e) => e.preventDefault()}>
        <AddItems onAdd={actions.onAdd} />
        <DropdownMenuSeparator className="-mx-1 my-1 h-px bg-[var(--hairline)]" />
        {folder ? (
          <>
            <DropdownMenuItem className={itemCls} onSelect={later(rename.begin)}><Pencil className={icon} strokeWidth={1.75} /> Rename folder</DropdownMenuItem>
            <DropdownMenuItem className={itemCls} onSelect={later(() => actions.edits.move(folder))}><FolderInput className={icon} strokeWidth={1.75} /> Move folder</DropdownMenuItem>
            <DropdownMenuItem className={`${itemCls} text-[var(--danger)] focus:text-[var(--danger)]`} onSelect={later(() => actions.edits.remove(folder))}>
              <Trash2 className={icon} strokeWidth={1.75} /> Delete folder
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem className={itemCls} onSelect={later(actions.onEditBase)}><Pencil className={icon} strokeWidth={1.75} /> Edit details</DropdownMenuItem>
            <DropdownMenuItem className={`${itemCls} text-[var(--danger)] focus:text-[var(--danger)]`} onSelect={later(actions.onDeleteBase)}>
              <Trash2 className={icon} strokeWidth={1.75} /> Delete knowledge base
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* Org / Knowledge / Base / Folder / … — every step back is a link, and the last is the open
   place with its menu. A file open below adds its name, plain: the file view has its own
   menu. A path wider than the header scrolls sideways, kept at its end. */
export function KbBreadcrumb({ base, path, file, onHome, onOpen, actions }: {
  base?: KnowledgeBase;
  /** Root-first folders down to the open one; empty at the base's top. */
  path: KbNode[];
  file?: KbNode;
  onHome: () => void;
  onOpen: (folderId: string | null) => void;
  actions: PlaceActions;
}) {
  const trail = useRef<HTMLElement>(null);
  const place = [...path.map((f) => f.id), file?.id].join("/");
  useEffect(() => {
    if (trail.current) trail.current.scrollLeft = trail.current.scrollWidth;
  }, [place]);
  const steps = file ? path : path.slice(0, -1);
  const folder = file ? undefined : path[path.length - 1];
  const atTop = !file && !folder;

  return (
    <nav ref={trail} aria-label="Breadcrumb" className={`${crumbRowCls} overflow-x-auto no-scrollbar`}>
      <OrgCrumb className="max-sm:hidden" />
      <button onClick={onHome} className={crumbCls} title="All knowledge">
        <Library className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
        <span className="hidden sm:inline">Knowledge</span>
      </button>
      <CrumbSlash />
      {!base ? (
        <span className="px-1.5 text-[13.5px] text-[var(--text-tertiary)]">…</span>
      ) : atTop ? (
        <Place base={base} actions={actions} />
      ) : (
        <button onClick={() => onOpen(null)} className={crumbCls} title={base.name}>
          {base.icon && <span className="shrink-0 text-[13px] leading-none">{base.icon}</span>}
          <span className="truncate">{base.name}</span>
        </button>
      )}
      {base &&
        steps.map((f) => (
          <React.Fragment key={f.id}>
            <CrumbSlash />
            <button onClick={() => onOpen(f.id)} className={crumbCls} title={f.name}>
              <span className="truncate">{f.name}</span>
            </button>
          </React.Fragment>
        ))}
      {base && folder && (
        <>
          <CrumbSlash />
          <Place key={folder.id} base={base} folder={folder} actions={actions} />
        </>
      )}
      {file && (
        <>
          <CrumbSlash />
          <span aria-current="page" title={file.name} className={currentCrumbCls}>
            <span className="truncate">{file.name}</span>
          </span>
        </>
      )}
    </nav>
  );
}
